import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Wifi, WifiOff, Cloud, CloudOff, X } from "lucide-react";
import { useAuth } from "../../Authentication/components/RealTimeAuthStatus";
import LoginModal from "./LoginModal";

const OnlineStatusIndicator: React.FC = () => {
  const [isOnline, setIsOnline] = useState(true);
  const [isDismissed, setIsDismissed] = useState(() => {
    // Check if dismissed in current session
    return sessionStorage.getItem("statusIndicatorDismissed") === "true";
  });
  const [showLoginModal, setShowLoginModal] = useState(false);
  const { session } = useAuth();
  const isAuthenticated = !!session;

  const handleDismiss = () => {
    setIsDismissed(true);
    sessionStorage.setItem("statusIndicatorDismissed", "true");
  };

  useEffect(() => {
    // Check online status
    setIsOnline(navigator.onLine);

    // Listen for online/offline events
    const handleOnline = () => {
      setIsOnline(true);
    };

    const handleOffline = () => {
      setIsOnline(false);
      setIsDismissed(false); // Reset dismiss when going offline
      sessionStorage.removeItem("statusIndicatorDismissed"); // Clear session storage too
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, []);

  return (
    <>
      <AnimatePresence>
        {isDismissed && !isAuthenticated && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{
              type: "spring",
              stiffness: 300,
              damping: 25,
            }}
            className="fixed top-4 right-4 z-50"
          >
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setShowLoginModal(true)}
              className="flex items-center px-3 py-1.5 backdrop-blur-lg rounded-full shadow-md border bg-white/80 border-sky-200/50 hover:bg-white/90 transition-colors cursor-pointer"
            >
              {/* Pulsing dot - no animation on mobile */}
              <motion.div
                animate={{
                  scale: [1, 1.2, 1],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatType: "reverse",
                }}
                className="relative mr-2"
              >
                <div className="w-2 h-2 rounded-full bg-sky-500" />
                <div className="absolute inset-0 w-2 h-2 rounded-full animate-ping bg-sky-400 sm:block hidden" />
              </motion.div>

              <CloudOff
                className="w-4 h-4 text-sky-600 mr-1.5"
                strokeWidth={2}
              />
              <span className="text-xs font-medium text-sky-700 mr-2">
                Local Storage
              </span>
              <span className="text-xs bg-sky-600 text-white px-2 py-0.5 rounded-full font-medium">
                Sign In
              </span>
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Login Modal */}
      <LoginModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
      />
    </>
  );
};

export default OnlineStatusIndicator;
